import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { mediaList } from "@/mocks/media";
import type { MediaItem } from "@/types/media";
import { TYPE_LABELS, TYPE_PATHS } from "@/constants/media";
import { useSeo } from "@/hooks/useSeo";
import { useFavorites } from "@/hooks/useFavorites";
import MediaCard from "@/components/feature/MediaCard";
import EmptyState from "@/components/base/EmptyState";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const allMedia = mediaList as MediaItem[];
  const item = allMedia.find((media) => media.id === id);

  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");
  const [shareOpen, setShareOpen] = useState(false);

  const related = useMemo(() => {
    if (!item) return [];
    return allMedia
      .filter((media) => media.id !== item.id)
      .map((media) => {
        let score = 0;
        if (media.type === item.type) score += 2;
        if (media.director === item.director) score += 3;
        score += media.genres.filter((genre) => item.genres.includes(genre)).length;
        return { media, score };
      })
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score || b.media.rating - a.media.rating)
      .slice(0, 4)
      .map((entry) => entry.media);
  }, [allMedia, item]);

  useSeo({
    title: item
      ? `${TYPE_LABELS[item.type]}《${item.title}》详情 - 星空传媒`
      : "作品详情 - 星空传媒",
    description: item
      ? `查看《${item.title}》剧情简介、主演及相关推荐，星空传媒为您推荐优质影视内容。`
      : "查看影视作品剧情简介、主演及相关推荐，星空传媒为您推荐优质影视内容。",
    keywords: item
      ? `星空传媒,${item.title},${TYPE_LABELS[item.type]},${item.genres.join(",")}`
      : "星空传媒,影视作品,影视详情",
  });

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast("链接已复制，可粘贴到微信中分享");
    } catch {
      showToast("复制失败，请手动复制浏览器地址");
    }
    setShareOpen(false);
  };

  const handleWeibo = () => {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(`星空传媒 · 《${item?.title ?? ""}》`);
    window.open(
      `https://service.weibo.com/share/share.php?url=${url}&title=${title}`,
      "_blank",
      "noopener,noreferrer",
    );
    setShareOpen(false);
  };

  if (!item) {
    return (
      <div className="mx-auto max-w-[1280px] px-4 md:px-6 py-16">
        <EmptyState
          icon="ri-film-line"
          title="未找到该影视作品"
          description="该作品可能已下线或链接有误，返回首页继续浏览其他内容吧。"
        />
        <div className="flex justify-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-accent-600 cursor-pointer"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const favorited = isFavorite(item.id);

  return (
    <div className="mx-auto max-w-[1280px] px-4 md:px-6 py-6 md:py-8">
      <nav className="flex flex-wrap items-center gap-1 text-sm text-foreground-500" aria-label="面包屑导航">
        <Link to="/" className="transition-colors hover:text-accent-600 cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link
          to={TYPE_PATHS[item.type]}
          className="transition-colors hover:text-accent-600 cursor-pointer"
        >
          {TYPE_LABELS[item.type]}
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-900">{item.title}</span>
      </nav>

      <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
        <div className="lg:col-span-1">
          <div className="overflow-hidden rounded-lg bg-secondary-100">
            <img
              src={item.cover}
              alt={`《${item.title}》封面`}
              title={`${item.title} - 星空传媒影视作品详情`}
              className="w-full object-cover object-top"
            />
          </div>
          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              onClick={() => toggleFavorite(item.id)}
              className={`flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium transition-colors cursor-pointer ${
                favorited
                  ? "bg-accent-500 text-white hover:bg-accent-600"
                  : "border border-secondary-300 text-foreground-700 hover:border-accent-300 hover:text-accent-600"
              }`}
            >
              <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
              {favorited ? "已收藏" : "收藏"}
            </button>
            <div className="relative flex-1">
              <button
                type="button"
                onClick={() => setShareOpen((prev) => !prev)}
                className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-secondary-300 px-4 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-accent-300 hover:text-accent-600 cursor-pointer"
              >
                <i className="ri-share-forward-line"></i>
                分享
              </button>
              {shareOpen && (
                <div className="absolute left-0 top-full z-30 mt-2 w-full min-w-[180px] overflow-hidden rounded-lg border border-secondary-200 bg-background-50 p-1 shadow-lg animate-scale-in">
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-foreground-700 transition-colors hover:bg-secondary-100 cursor-pointer"
                  >
                    <i className="ri-link text-accent-500"></i>
                    复制链接
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      showToast("请复制链接后在微信中分享");
                      setShareOpen(false);
                    }}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-foreground-700 transition-colors hover:bg-secondary-100 cursor-pointer"
                  >
                    <i className="ri-wechat-line text-accent-500"></i>
                    分享到微信
                  </button>
                  <button
                    type="button"
                    onClick={handleWeibo}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-foreground-700 transition-colors hover:bg-secondary-100 cursor-pointer"
                  >
                    <i className="ri-weibo-line text-accent-500"></i>
                    分享到微博
                  </button>
                </div>
              )}
            </div>
          </div>

          <dl className="mt-5 space-y-3 rounded-lg border border-secondary-200 bg-background-50 p-4 text-sm">
            <div className="flex gap-3">
              <dt className="w-16 shrink-0 text-foreground-400">类型</dt>
              <dd className="text-foreground-800">
                {TYPE_LABELS[item.type]} · {item.genres.join(" / ")}
              </dd>
            </div>
            <div className="flex gap-3">
              <dt className="w-16 shrink-0 text-foreground-400">导演</dt>
              <dd className="text-foreground-800">{item.director}</dd>
            </div>
            <div className="flex gap-3">
              <dt className="w-16 shrink-0 text-foreground-400">主演</dt>
              <dd className="text-foreground-800">{item.cast.join("、")}</dd>
            </div>
            <div className="flex gap-3">
              <dt className="w-16 shrink-0 text-foreground-400">年份</dt>
              <dd className="text-foreground-800">{item.year}</dd>
            </div>
            <div className="flex gap-3">
              <dt className="w-16 shrink-0 text-foreground-400">地区</dt>
              <dd className="text-foreground-800">{item.region}</dd>
            </div>
            {item.episodes && (
              <div className="flex gap-3">
                <dt className="w-16 shrink-0 text-foreground-400">集数</dt>
                <dd className="text-foreground-800">共 {item.episodes} 集</dd>
              </div>
            )}
            {item.duration && (
              <div className="flex gap-3">
                <dt className="w-16 shrink-0 text-foreground-400">片长</dt>
                <dd className="text-foreground-800">{item.duration}</dd>
              </div>
            )}
          </dl>
        </div>

        <div className="lg:col-span-2">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center rounded-md bg-primary-600 px-2.5 py-1 text-xs font-medium text-white">
              {TYPE_LABELS[item.type]}
            </span>
            <span className="inline-flex items-center gap-1 rounded-md bg-accent-100 px-2.5 py-1 text-xs font-semibold text-accent-700">
              <i className="ri-star-fill text-[10px]"></i>
              {item.rating.toFixed(1)} 分
            </span>
            <span className="flex items-center gap-1 text-xs text-foreground-500">
              <i className="ri-play-circle-line"></i>
              {item.views} 万次播放
            </span>
          </div>

          <h1 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-4xl">
            {item.title}
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            {item.year} · {item.region} · {item.genres.join(" · ")}
          </p>

          <section className="mt-6 rounded-lg border border-secondary-200 bg-background-50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="h-4 w-1 rounded-full bg-accent-500" />
              剧情简介
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-[15px]">
              {item.summary}
            </p>
            <p className="mt-3 rounded-md bg-secondary-50 px-3 py-2 text-xs text-foreground-500">
              提示：本页面仅提供作品信息与观看入口指引，演示模式暂不支持在线播放功能。
            </p>
          </section>

          <section className="mt-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                相关推荐
              </h2>
              <Link
                to={TYPE_PATHS[item.type]}
                className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-foreground-500 transition-colors hover:text-accent-600 cursor-pointer"
              >
                更多{TYPE_LABELS[item.type]}
                <i className="ri-arrow-right-s-line"></i>
              </Link>
            </div>
            {related.length === 0 ? (
              <EmptyState
                icon="ri-inbox-line"
                title="暂无相关推荐"
                description="该作品暂时没有可推荐的相关内容，去看看其他影视作品吧。"
              />
            ) : (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
                {related.map((media) => (
                  <MediaCard key={media.id} item={media} />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 rounded-full bg-foreground-950/90 px-5 py-2.5 text-sm text-background-50 shadow-lg animate-fade-in">
          {toast}
        </div>
      )}
    </div>
  );
}